import { html, nothing } from "lit";
import type { AppViewState } from "./app-view-state.ts";
import { parseAgentSessionKey } from "../../../src/routing/session-key.js";
import { refreshChatAvatar } from "./app-chat.ts";
import { renderUsageTab } from "./app-render-usage-tab.ts";
import { renderToasts } from "./components/toast.ts";
import { renderOnboarding } from "./components/onboarding.ts";
import { renderChatControls, renderTab, renderThemeToggle } from "./app-render.helpers.ts";
import { loadAgentFileContent, loadAgentFiles, saveAgentFile } from "./controllers/agent-files.ts";
import { loadAgentIdentities, loadAgentIdentity } from "./controllers/agent-identity.ts";
import { loadAgentSkills } from "./controllers/agent-skills.ts";
import { loadAgents } from "./controllers/agents.ts";
import { loadChannels } from "./controllers/channels.ts";
import { loadChatHistory } from "./controllers/chat.ts";
import {
  applyConfig,
  loadConfig,
  runUpdate,
  saveConfig,
  updateConfigFormValue,
  removeConfigFormValue,
} from "./controllers/config.ts";
import {
  loadCronRuns,
  toggleCronJob,
  runCronJob,
  removeCronJob,
  addCronJob,
} from "./controllers/cron.ts";
import { loadDebug, callDebugMethod } from "./controllers/debug.ts";
import {
  approveDevicePairing,
  loadDevices,
  rejectDevicePairing,
  revokeDeviceToken,
  rotateDeviceToken,
} from "./controllers/devices.ts";
import {
  loadExecApprovals,
  removeExecApprovalsFormValue,
  saveExecApprovals,
  updateExecApprovalsFormValue,
} from "./controllers/exec-approvals.ts";
import { loadLogs } from "./controllers/logs.ts";
import { loadModelCatalog } from "./controllers/model-catalog.ts";
import { loadActivityLog, loadActivityLogStats } from "./controllers/activity-log.ts";
import { loadNodes } from "./controllers/nodes.ts";
import { loadPresence } from "./controllers/presence.ts";
import {
  deleteSession,
  loadSessions,
  patchSession,
  toggleSessionTaskLog,
} from "./controllers/sessions.ts";
import {
  installSkill,
  loadSkills,
  saveSkillApiKey,
  updateSkillEdit,
  updateSkillEnabled,
} from "./controllers/skills.ts";
import { icons } from "./icons.ts";
import {
  normalizeBasePath,
  TAB_GROUPS,
  SIMPLE_TABS,
  subtitleForTab,
  titleForTab,
  iconForTab,
  pathForTab,
} from "./navigation.ts";
import { renderAgents } from "./views/agents.ts";
import { renderChannels } from "./views/channels.ts";
import { renderChat } from "./views/chat.ts";
import { renderConfig } from "./views/config.ts";
import { renderCron } from "./views/cron.ts";
import { renderDebug } from "./views/debug.ts";
import { renderHeartbeat } from "./views/heartbeat.ts";
import { loadHeartbeatAnalytics } from "./controllers/heartbeat.ts";
import { renderExecApprovalPrompt } from "./views/exec-approval.ts";
import { renderGatewayUrlConfirmation } from "./views/gateway-url-confirmation.ts";
import { renderInstances } from "./views/instances.ts";
import { renderLogs } from "./views/logs.ts";
import { renderActivityLog } from "./views/activity-log.ts";
import { renderNodes } from "./views/nodes.ts";
import { renderDashboard } from "./views/dashboard.ts";
import { loadQueueStatus } from "./controllers/queue-status.ts";
import { loadSubagentsStatus } from "./controllers/subagents-status.ts";
import { renderLearning } from "./views/learning.ts";
import {
  loadKnowledgeSources,
  createKnowledgeSource,
  updateKnowledgeSource,
  deleteKnowledgeSource,
  collectKnowledgeSource,
} from "./controllers/knowledge-sources.ts";
import type { KnowledgeSourceUI } from "./controllers/knowledge-sources.ts";
import {
  loadLearningData,
  loadMemoryFiles,
  loadMemoryFileContent,
  initLearningModule,
  initAllModules,
} from "./controllers/learning.ts";
import { renderMind } from "./views/mind.ts";
import { loadMindSectionData } from "./controllers/mind.ts";
import { renderOverview } from "./views/overview.ts";
import { renderSessions } from "./views/sessions.ts";
import { renderSkills } from "./views/skills.ts";
import { renderTerminal } from "./views/terminal.ts";
import { execTerminalCommand, clearTerminalHistory } from "./controllers/terminal.ts";
import { renderFiles } from "./views/files.ts";
import { renderSpawnMonitor } from "./views/spawn-monitor.ts";
import { loadDirectory, openFile, saveFile, closeFile, deleteItem } from "./controllers/files.ts";
import { t, setLocale, getLocale, LOCALES, type Locale } from "./i18n.ts";

const AVATAR_DATA_RE = /^data:/i;
const AVATAR_HTTP_RE = /^https?:\/\//i;

function hasStoredCredentials(state: AppViewState): boolean {
  return Boolean(
    state.settings.token?.trim() || state.settings.password?.trim() || state.password?.trim(),
  );
}

function renderLoginScreen(state: AppViewState) {
  const basePath = normalizeBasePath(state.basePath ?? "");
  let loginPassword = "";

  const handleLogin = () => {
    if (!loginPassword.trim()) return;
    state.applySettings({ ...state.settings, password: loginPassword.trim() });
    (state as unknown as { password: string }).password = loginPassword.trim();
    state.connect();
  };

  return html`
    <div class="login-screen" style="
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      background: var(--wallpaper);
      background-attachment: fixed;
      background-size: cover;
      padding: 24px;
      position: relative;
    ">
      <!-- Glass login card -->
      <div style="
        width: 100%;
        max-width: 380px;
        padding: 44px 36px 36px;
        border-radius: 24px;
        background: rgba(30, 30, 50, 0.45);
        backdrop-filter: saturate(180%) blur(32px);
        -webkit-backdrop-filter: saturate(180%) blur(32px);
        border: 1px solid rgba(255, 255, 255, 0.12);
        box-shadow: 0 20px 60px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08);
        animation: scale-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) backwards;
      ">
        <div style="text-align: center; margin-bottom: 32px;">
          <!-- App icon with glow -->
          <div style="
            width: 80px; height: 80px;
            margin: 0 auto 20px;
            background: linear-gradient(135deg, rgba(108,140,255,0.25) 0%, rgba(139,92,246,0.25) 100%);
            border-radius: 22px;
            display: flex; align-items: center; justify-content: center;
            box-shadow: 0 8px 32px rgba(108,140,255,0.2);
            border: 1px solid rgba(255,255,255,0.08);
          ">
            <img
              src=${basePath ? `${basePath}/favicon.svg` : "/favicon.svg"}
              alt="Nabe"
              style="width: 44px; height: 44px; filter: drop-shadow(0 2px 8px rgba(108,140,255,0.3));"
            />
          </div>
          <div style="font-size: 22px; font-weight: 600; color: rgba(255,255,255,0.95); letter-spacing: -0.02em;">
            ${t("login.title")}
          </div>
          <div style="font-size: 13px; color: rgba(255,255,255,0.4); margin-top: 6px;">
            ${t("login.subtitle")}
          </div>
        </div>

        ${
          state.lastError
            ? html`
          <div style="
            padding: 12px 16px;
            border-radius: 14px;
            background: rgba(255,107,107,0.12);
            border: 1px solid rgba(255,107,107,0.2);
            color: #ff6b6b;
            font-size: 13px;
            margin-bottom: 20px;
            line-height: 1.5;
          ">${state.lastError}</div>
        `
            : nothing
        }

        <!-- Password input -->
        <div style="margin-bottom: 20px;">
          <input
            type="password"
            placeholder="${t("login.password")}"
            autofocus
            style="
              width: 100%;
              padding: 14px 18px;
              border-radius: 14px;
              border: 1px solid rgba(255,255,255,0.1);
              background: rgba(255,255,255,0.06);
              backdrop-filter: blur(8px);
              -webkit-backdrop-filter: blur(8px);
              color: rgba(255,255,255,0.9);
              font-size: 15px;
              outline: none;
              box-sizing: border-box;
              transition: border-color 0.2s, box-shadow 0.2s;
            "
            @input=${(e: Event) => {
              loginPassword = (e.target as HTMLInputElement).value;
            }}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") handleLogin();
            }}
            @focus=${(e: Event) => {
              const el = e.target as HTMLInputElement;
              el.style.borderColor = "rgba(108,140,255,0.5)";
              el.style.boxShadow = "0 0 0 3px rgba(108,140,255,0.15)";
            }}
            @blur=${(e: Event) => {
              const el = e.target as HTMLInputElement;
              el.style.borderColor = "rgba(255,255,255,0.1)";
              el.style.boxShadow = "none";
            }}
          />
        </div>

        <!-- Sign in button with gradient -->
        <button
          style="
            width: 100%;
            padding: 14px 0;
            border-radius: 14px;
            border: none;
            background: linear-gradient(135deg, #6c8cff 0%, #8b5cf6 100%);
            color: #fff;
            font-size: 15px;
            font-weight: 600;
            cursor: pointer;
            transition: opacity 0.2s, transform 0.1s, box-shadow 0.2s;
            box-shadow: 0 4px 20px rgba(108,140,255,0.3);
          "
          @click=${handleLogin}
          @mousedown=${(e: Event) => {
            (e.target as HTMLButtonElement).style.transform = "scale(0.98)";
          }}
          @mouseup=${(e: Event) => {
            (e.target as HTMLButtonElement).style.transform = "scale(1)";
          }}
          @mouseleave=${(e: Event) => {
            (e.target as HTMLButtonElement).style.transform = "scale(1)";
          }}
          @mouseover=${(e: Event) => {
            (e.target as HTMLButtonElement).style.boxShadow = "0 6px 28px rgba(108,140,255,0.4)";
          }}
          @mouseout=${(e: Event) => {
            (e.target as HTMLButtonElement).style.boxShadow = "0 4px 20px rgba(108,140,255,0.3)";
          }}
        >${t("login.signin")}</button>

        <div style="
          margin-top: 20px;
          text-align: center;
          font-size: 11px;
          color: rgba(255,255,255,0.25);
        ">${t("login.hint")}</div>

        <!-- Language switcher on login -->
        <div style="
          margin-top: 24px;
          display: flex;
          justify-content: center;
          gap: 6px;
          flex-wrap: wrap;
        ">
          ${LOCALES.map(
            (loc) => html`
              <button
                style="
                  padding: 4px 10px;
                  border-radius: 10px;
                  border: 1px solid ${getLocale() === loc.code ? "rgba(108,140,255,0.5)" : "rgba(255,255,255,0.08)"};
                  background: ${getLocale() === loc.code ? "rgba(108,140,255,0.15)" : "rgba(255,255,255,0.04)"};
                  color: ${getLocale() === loc.code ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.45)"};
                  font-size: 11px;
                  cursor: pointer;
                  transition: all 0.2s;
                "
                @click=${() => {
                  setLocale(loc.code);
                  state.applySettings({ ...state.settings, locale: loc.code });
                }}
              >${loc.flag} ${loc.label}</button>
            `,
          )}
        </div>
      </div>
    </div>
  `;
}

function renderLanguageSwitcher(state: AppViewState) {
  const current = LOCALES.find((l) => l.code === getLocale()) ?? LOCALES[0];
  return html`
    <div style="position: relative; display: inline-block;">
      <button
        class="btn btn--small"
        style="
          font-size: 12px;
          padding: 4px 10px;
          border-radius: var(--radius-full);
          min-height: 28px;
          display: flex;
          align-items: center;
          gap: 4px;
        "
        title="${t("common.language")}"
        @click=${(e: Event) => {
          e.stopPropagation();
          const btn = e.currentTarget as HTMLElement;
          const dropdown = btn.nextElementSibling as HTMLElement;
          if (!dropdown) return;
          const isOpen = dropdown.style.display === "block";
          dropdown.style.display = isOpen ? "none" : "block";
          if (!isOpen) {
            const closeDropdown = (ev: MouseEvent) => {
              if (!dropdown.contains(ev.target as Node) && ev.target !== btn) {
                dropdown.style.display = "none";
                document.removeEventListener("click", closeDropdown);
              }
            };
            setTimeout(() => document.addEventListener("click", closeDropdown), 0);
          }
        }}
      >${current.flag} <span style="font-size: 11px;">${current.label}</span></button>
      <div style="
        display: none;
        position: absolute;
        top: 100%;
        right: 0;
        margin-top: 6px;
        min-width: 140px;
        padding: 6px;
        border-radius: var(--radius-md);
        background: var(--glass-strong);
        backdrop-filter: saturate(180%) blur(var(--glass-blur));
        -webkit-backdrop-filter: saturate(180%) blur(var(--glass-blur));
        border: 1px solid var(--glass-border);
        box-shadow: var(--shadow-lg);
        z-index: 1000;
      ">
        ${LOCALES.map(
          (loc) => html`
            <button
              style="
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                padding: 8px 12px;
                border: none;
                border-radius: var(--radius-sm);
                background: ${getLocale() === loc.code ? "rgba(108,140,255,0.15)" : "transparent"};
                color: ${getLocale() === loc.code ? "var(--accent)" : "var(--text)"};
                font-size: 13px;
                cursor: pointer;
                transition: background 0.15s;
                text-align: left;
              "
              @click=${(e: Event) => {
                e.stopPropagation();
                setLocale(loc.code);
                state.applySettings({ ...state.settings, locale: loc.code });
                const dropdown = (e.currentTarget as HTMLElement).closest(
                  "div[style*='position: absolute']",
                ) as HTMLElement;
                if (dropdown) dropdown.style.display = "none";
              }}
              @mouseover=${(e: Event) => {
                if (getLocale() !== loc.code)
                  (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.06)";
              }}
              @mouseout=${(e: Event) => {
                if (getLocale() !== loc.code)
                  (e.currentTarget as HTMLElement).style.background = "transparent";
              }}
            >${loc.flag} ${loc.label}</button>
          `,
        )}
      </div>
    </div>
  `;
}

function resolveAssistantAvatarUrl(state: AppViewState): string | undefined {
  const list = state.agentsList?.agents ?? [];
  const parsed = parseAgentSessionKey(state.sessionKey);
  const agentId = parsed?.agentId ?? state.agentsList?.defaultId ?? "main";
  const agent = list.find((entry) => entry.id === agentId);
  const identity = agent?.identity;
  const candidate = identity?.avatarUrl ?? identity?.avatar;
  if (!candidate) {
    return undefined;
  }
  if (AVATAR_DATA_RE.test(candidate) || AVATAR_HTTP_RE.test(candidate)) {
    return candidate;
  }
  return identity?.avatarUrl;
}

function renderSimpleNav(state: AppViewState) {
  const isChildActive = (entry: (typeof SIMPLE_TABS)[number]) =>
    entry.children?.includes(state.tab) ?? false;

  return html`
    ${SIMPLE_TABS.map((entry) => {
      const active = state.tab === entry.tab || isChildActive(entry);
      const href = pathForTab(entry.tab, state.basePath);
      const hasChildren = entry.children && entry.children.length > 0;
      const childExpanded = active && hasChildren;
      return html`
        <div class="nav-simple-group">
          <a
            href=${href}
            class="nav-item nav-item--simple ${active ? "active" : ""}"
            @click=${(event: MouseEvent) => {
              if (
                event.defaultPrevented ||
                event.button !== 0 ||
                event.metaKey ||
                event.ctrlKey ||
                event.shiftKey ||
                event.altKey
              ) {
                return;
              }
              event.preventDefault();
              state.setTab(entry.tab);
            }}
            title=${t(entry.labelKey as any) || entry.labelKey}
          >
            <span class="nav-item__icon" aria-hidden="true">${icons[entry.icon]}</span>
            <span class="nav-item__text">${t(entry.labelKey as any) || entry.labelKey}</span>
            ${hasChildren ? html`<span class="nav-item__chevron" aria-hidden="true">${childExpanded ? "−" : "+"}</span>` : nothing}
          </a>
          ${
            childExpanded && entry.children
              ? html`
            <div class="nav-simple-children">
              ${entry.children.map((childTab) => {
                const childHref = pathForTab(childTab, state.basePath);
                const childActive = state.tab === childTab;
                return html`
                  <a
                    href=${childHref}
                    class="nav-item nav-item--child ${childActive ? "active" : ""}"
                    @click=${(event: MouseEvent) => {
                      if (
                        event.defaultPrevented ||
                        event.button !== 0 ||
                        event.metaKey ||
                        event.ctrlKey ||
                        event.shiftKey ||
                        event.altKey
                      ) {
                        return;
                      }
                      event.preventDefault();
                      state.setTab(childTab);
                    }}
                    title=${titleForTab(childTab)}
                  >
                    <span class="nav-item__icon" aria-hidden="true">${icons[iconForTab(childTab)]}</span>
                    <span class="nav-item__text">${titleForTab(childTab)}</span>
                  </a>
                `;
              })}
            </div>
          `
              : nothing
          }
        </div>
      `;
    })}
  `;
}

function renderNavModeToggle(state: AppViewState) {
  const isSimple = state.settings.navMode === "simple";
  return html`
    <div class="nav-mode-toggle" role="group" aria-label="${t("nav.mode.toggle_label" as any)}">
      <button
        class="nav-mode-btn ${isSimple ? "active" : ""}"
        @click=${() => state.applySettings({ ...state.settings, navMode: "simple" })}
        title="${t("nav.mode.simple" as any)}"
        aria-pressed=${isSimple}
      >${t("nav.mode.simple" as any)}</button>
      <button
        class="nav-mode-btn ${!isSimple ? "active" : ""}"
        @click=${() => state.applySettings({ ...state.settings, navMode: "advanced" })}
        title="${t("nav.mode.advanced" as any)}"
        aria-pressed=${!isSimple}
      >${t("nav.mode.advanced" as any)}</button>
    </div>
  `;
}

export function renderApp(state: AppViewState) {
  // Show login screen when not connected and no credentials stored
  if (!state.connected && !hasStoredCredentials(state)) {
    return renderLoginScreen(state);
  }

  const presenceCount = state.presenceEntries.length;
  const sessionsCount = state.sessionsResult?.count ?? null;
  const cronNext = state.cronStatus?.nextWakeAtMs ?? null;
  const chatDisabledReason = state.connected ? null : "Disconnected from gateway.";
  const isChat = state.tab === "chat";
  const chatFocus = isChat && (state.settings.chatFocusMode || state.onboarding);
  const showThinking = state.onboarding ? false : state.settings.chatShowThinking;
  const assistantAvatarUrl = resolveAssistantAvatarUrl(state);
  const chatAvatarUrl = state.chatAvatarUrl ?? assistantAvatarUrl ?? null;
  const configValue =
    state.configForm ?? (state.configSnapshot?.config as Record<string, unknown> | null);
  const basePath = normalizeBasePath(state.basePath ?? "");
  const resolvedAgentId =
    state.agentsSelectedId ??
    state.agentsList?.defaultId ??
    state.agentsList?.agents?.[0]?.id ??
    null;

  return html`
    <a class="skip-nav" href="#main-content">${t("app.skip_to_content" as any) || "Skip to content"}</a>
    <div class="shell ${isChat ? "shell--chat" : ""} ${chatFocus ? "shell--chat-focus" : ""} ${state.settings.navCollapsed ? "shell--nav-collapsed" : ""} ${state.onboarding ? "shell--onboarding" : ""}" role="application" aria-label="Nabe">
      <header class="topbar" role="banner">
        <div class="topbar-left">
          <button
            class="nav-collapse-toggle"
            @click=${() =>
              state.applySettings({
                ...state.settings,
                navCollapsed: !state.settings.navCollapsed,
              })}
            title="${state.settings.navCollapsed ? t("app.expand_sidebar") : t("app.collapse_sidebar")}"
            aria-label="${state.settings.navCollapsed ? t("app.expand_sidebar") : t("app.collapse_sidebar")}"
          >
            <span class="nav-collapse-toggle__icon">${icons.menu}</span>
          </button>
          <div class="brand">
            <div class="brand-logo">
              <img src=${basePath ? `${basePath}/favicon.svg` : "/favicon.svg"} alt="Nabe" />
            </div>
            <div class="brand-text">
              <div class="brand-title">${t("app.brand")}</div>
              <div class="brand-sub">${t("app.subtitle")}</div>
            </div>
          </div>
        </div>
        <div class="topbar-status">
          <div class="pill" title="${state.connected ? t("topbar.connected") : t("topbar.offline")}">
            <span class="statusDot ${state.connected ? "ok" : ""}"></span>
            <span class="mono">${state.connected ? t("topbar.connected") : t("topbar.offline")}</span>
          </div>
          ${renderThemeToggle(state)}
          ${renderLanguageSwitcher(state)}
          <button
            class="btn btn--small"
            style="font-size: 12px; padding: 4px 12px; border-radius: var(--radius-full); min-height: 28px;"
            title="${t("topbar.signout")}"
            @click=${() => {
              state.applySettings({ ...state.settings, password: "", token: "" });
              (state as unknown as { password: string }).password = "";
              state.client?.stop();
              window.location.reload();
            }}
          >${t("topbar.signout")}</button>
        </div>
      </header>
      <aside class="nav ${state.settings.navCollapsed ? "nav--collapsed" : ""}" role="navigation" aria-label="Main navigation">
        ${
          state.settings.navMode === "simple"
            ? renderSimpleNav(state)
            : TAB_GROUPS.map((group) => {
                const isGroupCollapsed = state.settings.navGroupsCollapsed[group.labelKey] ?? false;
                const hasActiveTab = group.tabs.some((tab) => tab === state.tab);
                return html`
            <div class="nav-group ${isGroupCollapsed && !hasActiveTab ? "nav-group--collapsed" : ""}">
              <button
                class="nav-label"
                @click=${() => {
                  const next = { ...state.settings.navGroupsCollapsed };
                  next[group.labelKey] = !isGroupCollapsed;
                  state.applySettings({
                    ...state.settings,
                    navGroupsCollapsed: next,
                  });
                }}
                aria-expanded=${!isGroupCollapsed}
              >
                <span class="nav-label__text">${t(group.labelKey)}</span>
                <span class="nav-label__chevron">${isGroupCollapsed ? "+" : "−"}</span>
              </button>
              <div class="nav-group__items">
                ${group.tabs.map((tab) => renderTab(state, tab))}
              </div>
            </div>
          `;
              })
        }
        ${renderNavModeToggle(state)}
      </aside>
      <main id="main-content" class="content ${isChat ? "content--chat" : ""}" role="main">
        <section class="content-header">
          <div>
            ${state.tab === "usage" ? nothing : html`<div class="page-title">${titleForTab(state.tab)}</div>`}
            ${state.tab === "usage" ? nothing : html`<div class="page-sub">${subtitleForTab(state.tab)}</div>`}
          </div>
          <div class="page-meta">
            ${state.lastError ? html`<div class="pill danger">${state.lastError}</div>` : nothing}
            ${isChat ? renderChatControls(state) : nothing}
          </div>
        </section>

        ${
          state.tab === "overview"
            ? renderOverview({
                connected: state.connected,
                hello: state.hello,
                settings: state.settings,
                password: state.password,
                lastError: state.lastError,
                presenceCount,
                sessionsCount,
                cronEnabled: state.cronStatus?.enabled ?? null,
                cronNext,
                lastChannelsRefresh: state.channelsLastSuccess,
                onSettingsChange: (next) => state.applySettings(next),
                onPasswordChange: (next) => (state.password = next),
                onSessionKeyChange: (next) => {
                  state.sessionKey = next;
                  state.chatMessage = "";
                  state.resetToolStream();
                  state.applySettings({
                    ...state.settings,
                    sessionKey: next,
                    lastActiveSessionKey: next,
                  });
                  void state.loadAssistantIdentity();
                },
                onConnect: () => state.connect(),
                onRefresh: () => state.loadOverview(),
              })
            : nothing
        }

        ${
          state.tab === "dashboard"
            ? renderDashboard({
                connected: state.connected,
                nodesLoading: state.nodesLoading,
                nodes: state.nodes.map((n) => ({
                  nodeId: String(n.nodeId ?? ""),
                  displayName: typeof n.displayName === "string" ? n.displayName : undefined,
                  platform: typeof n.platform === "string" ? n.platform : undefined,
                  remoteIp: typeof n.remoteIp === "string" ? n.remoteIp : undefined,
                  caps: Array.isArray(n.caps) ? n.caps.map(String) : [],
                  commands: Array.isArray(n.commands) ? n.commands.map(String) : [],
                  connected: Boolean(n.connected),
                  paired: Boolean(n.paired),
                  connectedAtMs: typeof n.connectedAtMs === "number" ? n.connectedAtMs : undefined,
                  version: typeof n.version === "string" ? n.version : undefined,
                  coreVersion: typeof n.coreVersion === "string" ? n.coreVersion : undefined,
                })),
                agentsLoading: state.agentsLoading,
                agents: (state.agentsList?.agents ?? []).map((a) => ({
                  id: a.id,
                  identity: a.identity as
                    | { name?: string; avatar?: string; avatarUrl?: string }
                    | undefined,
                })),
                sessions: state.sessionsResult ? { count: state.sessionsResult.count } : null,
                presenceCount: state.presenceEntries.length,
                queueStatus: state.queueStatus,
                queueStatusLoading: state.queueStatusLoading,
                onRefresh: () => {
                  void state.handleLoadNodes();
                  void loadQueueStatus(state as unknown as Parameters<typeof loadQueueStatus>[0]);
                },
              })
            : nothing
        }

        ${
          state.tab === "channels"
            ? renderChannels({
                connected: state.connected,
                loading: state.channelsLoading,
                snapshot: state.channelsSnapshot,
                lastError: state.channelsError,
                lastSuccessAt: state.channelsLastSuccess,
                whatsappMessage: state.whatsappLoginMessage,
                whatsappQrDataUrl: state.whatsappLoginQrDataUrl,
                whatsappConnected: state.whatsappLoginConnected,
                whatsappBusy: state.whatsappBusy,
                configSchema: state.configSchema,
                configSchemaLoading: state.configSchemaLoading,
                configForm: state.configForm,
                configUiHints: state.configUiHints,
                configSaving: state.configSaving,
                configFormDirty: state.configFormDirty,
                nostrProfileFormState: state.nostrProfileFormState,
                nostrProfileAccountId: state.nostrProfileAccountId,
                onRefresh: (probe) => loadChannels(state, probe),
                onWhatsAppStart: (force) => state.handleWhatsAppStart(force),
                onWhatsAppWait: () => state.handleWhatsAppWait(),
                onWhatsAppLogout: () => state.handleWhatsAppLogout(),
                onConfigPatch: (path, value) => updateConfigFormValue(state, path, value),
                onConfigSave: () => state.handleChannelConfigSave(),
                onConfigApply: () => state.handleChannelConfigApply(),
                onConfigReload: () => state.handleChannelConfigReload(),
                onNostrProfileEdit: (accountId, profile) =>
                  state.handleNostrProfileEdit(accountId, profile),
                onNostrProfileCancel: () => state.handleNostrProfileCancel(),
                onNostrProfileFieldChange: (field, value) =>
                  state.handleNostrProfileFieldChange(field, value),
                onNostrProfileSave: () => state.handleNostrProfileSave(),
                onNostrProfileImport: () => state.handleNostrProfileImport(),
                onNostrProfileToggleAdvanced: () => state.handleNostrProfileToggleAdvanced(),
              })
            : nothing
        }

        ${
          state.tab === "instances"
            ? renderInstances({
                loading: state.presenceLoading,
                entries: state.presenceEntries,
                lastError: state.presenceError,
                statusMessage: state.presenceStatus,
                onRefresh: () => loadPresence(state),
              })
            : nothing
        }

        ${
          state.tab === "sessions"
            ? renderSessions({
                loading: state.sessionsLoading,
                result: state.sessionsResult,
                error: state.sessionsError,
                activeMinutes: state.sessionsFilterActive,
                limit: state.sessionsFilterLimit,
                includeGlobal: state.sessionsIncludeGlobal,
                includeUnknown: state.sessionsIncludeUnknown,
                basePath: state.basePath,
                expandedKey: state.sessionsExpandedKey,
                expandedMessages: state.sessionsExpandedMessages,
                expandedLoading: state.sessionsExpandedLoading,
                onFiltersChange: (next) => {
                  state.sessionsFilterActive = next.activeMinutes;
                  state.sessionsFilterLimit = next.limit;
                  state.sessionsIncludeGlobal = next.includeGlobal;
                  state.sessionsIncludeUnknown = next.includeUnknown;
                },
                onRefresh: () => loadSessions(state),
                onPatch: (key, patch) => patchSession(state, key, patch),
                onDelete: (key) => deleteSession(state, key),
                onToggleTaskLog: (key) => toggleSessionTaskLog(state, key),
              })
            : nothing
        }

        ${renderUsageTab(state)}

        ${
          state.tab === "cron"
            ? renderCron({
                basePath: state.basePath,
                loading: state.cronLoading,
                status: state.cronStatus,
                jobs: state.cronJobs,
                error: state.cronError,
                busy: state.cronBusy,
                form: state.cronForm,
                channels: state.channelsSnapshot?.channelMeta?.length
                  ? state.channelsSnapshot.channelMeta.map((entry) => entry.id)
                  : (state.channelsSnapshot?.channelOrder ?? []),
                channelLabels: state.channelsSnapshot?.channelLabels ?? {},
                channelMeta: state.channelsSnapshot?.channelMeta ?? [],
                runsJobId: state.cronRunsJobId,
                runs: state.cronRuns,
                onFormChange: (patch) => (state.cronForm = { ...state.cronForm, ...patch }),
                onRefresh: () => state.loadCron(),
                onAdd: () => addCronJob(state),
                onToggle: (job, enabled) => toggleCronJob(state, job, enabled),
                onRun: (job) => runCronJob(state, job),
                onRemove: (job) => removeCronJob(state, job),
                onLoadRuns: (jobId) => loadCronRuns(state, jobId),
              })
            : nothing
        }

        ${
          state.tab === "agents"
            ? renderAgents({
                loading: state.agentsLoading,
                error: state.agentsError,
                agentsList: state.agentsList,
                selectedAgentId: resolvedAgentId,
                activePanel: state.agentsPanel,
                configForm: configValue,
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                channelsLoading: state.channelsLoading,
                channelsError: state.channelsError,
                channelsSnapshot: state.channelsSnapshot,
                channelsLastSuccess: state.channelsLastSuccess,
                cronLoading: state.cronLoading,
                cronStatus: state.cronStatus,
                cronJobs: state.cronJobs,
                cronError: state.cronError,
                agentFilesLoading: state.agentFilesLoading,
                agentFilesError: state.agentFilesError,
                agentFilesList: state.agentFilesList,
                agentFileActive: state.agentFileActive,
                agentFileContents: state.agentFileContents,
                agentFileDrafts: state.agentFileDrafts,
                agentFileSaving: state.agentFileSaving,
                agentIdentityLoading: state.agentIdentityLoading,
                agentIdentityError: state.agentIdentityError,
                agentIdentityById: state.agentIdentityById,
                agentSkillsLoading: state.agentSkillsLoading,
                agentSkillsReport: state.agentSkillsReport,
                agentSkillsError: state.agentSkillsError,
                agentSkillsAgentId: state.agentSkillsAgentId,
                skillsFilter: state.skillsFilter,
                modelCatalogAll: state.modelCatalogAll,
                modelCatalogLoading: state.modelCatalogLoading,
                onLoadModelCatalog: () => loadModelCatalog(state),
                onRefresh: async () => {
                  await loadAgents(state);
                  const agentIds = state.agentsList?.agents?.map((entry) => entry.id) ?? [];
                  if (agentIds.length > 0) {
                    void loadAgentIdentities(state, agentIds);
                  }
                },
                onSelectAgent: (agentId) => {
                  if (state.agentsSelectedId === agentId) {
                    return;
                  }
                  state.agentsSelectedId = agentId;
                  state.agentFilesList = null;
                  state.agentFilesError = null;
                  state.agentFilesLoading = false;
                  state.agentFileActive = null;
                  state.agentFileContents = {};
                  state.agentFileDrafts = {};
                  state.agentSkillsReport = null;
                  state.agentSkillsError = null;
                  state.agentSkillsAgentId = null;
                  void loadAgentIdentity(state, agentId);
                  if (state.agentsPanel === "files") {
                    void loadAgentFiles(state, agentId);
                  }
                  if (state.agentsPanel === "skills") {
                    void loadAgentSkills(state, agentId);
                  }
                },
                onSelectPanel: (panel) => {
                  state.agentsPanel = panel;
                  if (panel === "files" && resolvedAgentId) {
                    if (state.agentFilesList?.agentId !== resolvedAgentId) {
                      state.agentFilesList = null;
                      state.agentFilesError = null;
                      state.agentFileActive = null;
                      state.agentFileContents = {};
                      state.agentFileDrafts = {};
                      void loadAgentFiles(state, resolvedAgentId);
                    }
                  }
                  if (panel === "skills") {
                    if (resolvedAgentId) {
                      void loadAgentSkills(state, resolvedAgentId);
                    }
                  }
                  if (panel === "channels") {
                    void loadChannels(state, false);
                  }
                  if (panel === "cron") {
                    void state.loadCron();
                  }
                  if (panel === "runtime") {
                    void loadSubagentsStatus(
                      state as unknown as Parameters<typeof loadSubagentsStatus>[0],
                    );
                    void loadQueueStatus(state as unknown as Parameters<typeof loadQueueStatus>[0]);
                  }
                },
                onLoadFiles: (agentId) => loadAgentFiles(state, agentId),
                onSelectFile: (name) => {
                  state.agentFileActive = name;
                  if (!resolvedAgentId) {
                    return;
                  }
                  void loadAgentFileContent(state, resolvedAgentId, name);
                },
                onFileDraftChange: (name, content) => {
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: content };
                },
                onFileReset: (name) => {
                  const base = state.agentFileContents[name] ?? "";
                  state.agentFileDrafts = { ...state.agentFileDrafts, [name]: base };
                },
                onFileSave: (name) => {
                  if (!resolvedAgentId) {
                    return;
                  }
                  const content =
                    state.agentFileDrafts[name] ?? state.agentFileContents[name] ?? "";
                  void saveAgentFile(state, resolvedAgentId, name, content);
                },
                onToolsProfileChange: (agentId, profile, clearAllow) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "tools"];
                  if (profile) {
                    updateConfigFormValue(state, [...basePath, "profile"], profile);
                  } else {
                    removeConfigFormValue(state, [...basePath, "profile"]);
                  }
                  if (clearAllow) {
                    removeConfigFormValue(state, [...basePath, "allow"]);
                  }
                },
                onToolsOverridesChange: (agentId, alsoAllow, deny) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "tools"];
                  if (alsoAllow.length > 0) {
                    updateConfigFormValue(state, [...basePath, "alsoAllow"], alsoAllow);
                  } else {
                    removeConfigFormValue(state, [...basePath, "alsoAllow"]);
                  }
                  if (deny.length > 0) {
                    updateConfigFormValue(state, [...basePath, "deny"], deny);
                  } else {
                    removeConfigFormValue(state, [...basePath, "deny"]);
                  }
                },
                onConfigReload: () => loadConfig(state),
                onConfigSave: () => saveConfig(state),
                onChannelsRefresh: () => loadChannels(state, false),
                onCronRefresh: () => state.loadCron(),
                onSkillsFilterChange: (next) => (state.skillsFilter = next),
                onSkillsRefresh: () => {
                  if (resolvedAgentId) {
                    void loadAgentSkills(state, resolvedAgentId);
                  }
                },
                onAgentSkillToggle: (agentId, skillName, enabled) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const entry = list[index] as { skills?: unknown };
                  const normalizedSkill = skillName.trim();
                  if (!normalizedSkill) {
                    return;
                  }
                  const allSkills =
                    state.agentSkillsReport?.skills?.map((skill) => skill.name).filter(Boolean) ??
                    [];
                  const existing = Array.isArray(entry.skills)
                    ? entry.skills.map((name) => String(name).trim()).filter(Boolean)
                    : undefined;
                  const base = existing ?? allSkills;
                  const next = new Set(base);
                  if (enabled) {
                    next.add(normalizedSkill);
                  } else {
                    next.delete(normalizedSkill);
                  }
                  updateConfigFormValue(state, ["agents", "list", index, "skills"], [...next]);
                },
                onAgentSkillsClear: (agentId) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  removeConfigFormValue(state, ["agents", "list", index, "skills"]);
                },
                onAgentSkillsDisableAll: (agentId) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  updateConfigFormValue(state, ["agents", "list", index, "skills"], []);
                },
                // Runtime tab props
                subagentsStatus: state.subagentsStatus,
                subagentsStatusLoading: state.subagentsStatusLoading,
                queueStatus: state.queueStatus,
                queueStatusLoading: state.queueStatusLoading,
                onRuntimeRefresh: () => {
                  void loadSubagentsStatus(
                    state as unknown as Parameters<typeof loadSubagentsStatus>[0],
                  );
                  void loadQueueStatus(state as unknown as Parameters<typeof loadQueueStatus>[0]);
                },
                onModelChange: (agentId, modelId) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "model"];
                  if (!modelId) {
                    removeConfigFormValue(state, basePath);
                    return;
                  }
                  const entry = list[index] as { model?: unknown };
                  const existing = entry?.model;
                  if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                    const fallbacks = (existing as { fallbacks?: unknown }).fallbacks;
                    const next = {
                      primary: modelId,
                      ...(Array.isArray(fallbacks) ? { fallbacks } : {}),
                    };
                    updateConfigFormValue(state, basePath, next);
                  } else {
                    updateConfigFormValue(state, basePath, modelId);
                  }
                },
                onModelFallbacksChange: (agentId, fallbacks) => {
                  if (!configValue) {
                    return;
                  }
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) {
                    return;
                  }
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) {
                    return;
                  }
                  const basePath = ["agents", "list", index, "model"];
                  const entry = list[index] as { model?: unknown };
                  const normalized = fallbacks.map((name) => name.trim()).filter(Boolean);
                  const existing = entry.model;
                  const resolvePrimary = () => {
                    if (typeof existing === "string") {
                      return existing.trim() || null;
                    }
                    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                      const primary = (existing as { primary?: unknown }).primary;
                      if (typeof primary === "string") {
                        const trimmed = primary.trim();
                        return trimmed || null;
                      }
                    }
                    return null;
                  };
                  const primary = resolvePrimary();
                  if (normalized.length === 0) {
                    if (primary) {
                      updateConfigFormValue(state, basePath, primary);
                    } else {
                      removeConfigFormValue(state, basePath);
                    }
                    return;
                  }
                  const next = primary
                    ? { primary, fallbacks: normalized }
                    : { fallbacks: normalized };
                  updateConfigFormValue(state, basePath, next);
                },
                onApiKeyChange: (envVar: string, value: string) => {
                  updateConfigFormValue(state, ["env", "vars", envVar], value);
                },
                onCatalogAdd: (modelId: string, alias?: string) => {
                  const entry = alias ? { alias } : {};
                  updateConfigFormValue(state, ["agents", "defaults", "models", modelId], entry);
                },
                onCatalogRemove: (modelId: string) => {
                  removeConfigFormValue(state, ["agents", "defaults", "models", modelId]);
                },
                onCatalogEditAlias: (modelId: string, alias: string) => {
                  const entry = alias ? { alias } : {};
                  updateConfigFormValue(state, ["agents", "defaults", "models", modelId], entry);
                },
                onFallbackAdd: (agentId: string, modelId: string) => {
                  if (!configValue) return;
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) return;
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) return;
                  const basePath = ["agents", "list", index, "model"];
                  const entry = list[index] as { model?: unknown };
                  const existing = entry.model;
                  const resolvePrimary = () => {
                    if (typeof existing === "string") return existing.trim() || null;
                    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                      const primary = (existing as { primary?: unknown }).primary;
                      if (typeof primary === "string") return primary.trim() || null;
                    }
                    return null;
                  };
                  const resolveFallbacks = () => {
                    if (!existing || typeof existing === "string") return [];
                    if (typeof existing === "object" && !Array.isArray(existing)) {
                      const fb = (existing as { fallbacks?: unknown }).fallbacks;
                      return Array.isArray(fb)
                        ? fb.filter((v): v is string => typeof v === "string")
                        : [];
                    }
                    return [];
                  };
                  const primary = resolvePrimary();
                  const fallbacks = [...resolveFallbacks(), modelId];
                  const next = primary ? { primary, fallbacks } : { fallbacks };
                  updateConfigFormValue(state, basePath, next);
                },
                onFallbackRemove: (agentId: string, removeIndex: number) => {
                  if (!configValue) return;
                  const list = (configValue as { agents?: { list?: unknown[] } }).agents?.list;
                  if (!Array.isArray(list)) return;
                  const index = list.findIndex(
                    (entry) =>
                      entry &&
                      typeof entry === "object" &&
                      "id" in entry &&
                      (entry as { id?: string }).id === agentId,
                  );
                  if (index < 0) return;
                  const basePath = ["agents", "list", index, "model"];
                  const entry = list[index] as { model?: unknown };
                  const existing = entry.model;
                  const resolvePrimary = () => {
                    if (typeof existing === "string") return existing.trim() || null;
                    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
                      const primary = (existing as { primary?: unknown }).primary;
                      if (typeof primary === "string") return primary.trim() || null;
                    }
                    return null;
                  };
                  const resolveFallbacks = () => {
                    if (!existing || typeof existing === "string") return [];
                    if (typeof existing === "object" && !Array.isArray(existing)) {
                      const fb = (existing as { fallbacks?: unknown }).fallbacks;
                      return Array.isArray(fb)
                        ? fb.filter((v): v is string => typeof v === "string")
                        : [];
                    }
                    return [];
                  };
                  const primary = resolvePrimary();
                  const fallbacks = resolveFallbacks().filter((_, i) => i !== removeIndex);
                  if (fallbacks.length === 0) {
                    if (primary) {
                      updateConfigFormValue(state, basePath, primary);
                    } else {
                      removeConfigFormValue(state, basePath);
                    }
                  } else {
                    const next = primary ? { primary, fallbacks } : { fallbacks };
                    updateConfigFormValue(state, basePath, next);
                  }
                },
                onCustomEnvVarAdd: (key: string, value: string) => {
                  updateConfigFormValue(state, ["env", "vars", key], value);
                },
                onCustomEnvVarRemove: (key: string) => {
                  removeConfigFormValue(state, ["env", "vars", key]);
                },
              })
            : nothing
        }

        ${
          state.tab === "skills"
            ? renderSkills({
                loading: state.skillsLoading,
                report: state.skillsReport,
                error: state.skillsError,
                filter: state.skillsFilter,
                edits: state.skillEdits,
                messages: state.skillMessages,
                busyKey: state.skillsBusyKey,
                onFilterChange: (next) => (state.skillsFilter = next),
                onRefresh: () => loadSkills(state, { clearMessages: true }),
                onToggle: (key, enabled) => updateSkillEnabled(state, key, enabled),
                onEdit: (key, value) => updateSkillEdit(state, key, value),
                onSaveKey: (key) => saveSkillApiKey(state, key),
                onInstall: (skillKey, name, installId) =>
                  installSkill(state, skillKey, name, installId),
              })
            : nothing
        }

        ${
          state.tab === "learning"
            ? renderLearning({
                connected: state.connected,
                loading: state.learningLoading,
                error: state.learningError,
                memory: state.learningMemory,
                soul: state.learningSoul,
                identity: state.learningIdentity,
                user: state.learningUser,
                emotionalState: state.learningEmotionalState,
                userProfile: state.learningUserProfile,
                tieredStats: state.learningTieredStats,
                activeSection: state.learningActiveSection,
                initializing: state.learningInitializing,
                memoryFiles: state.learningMemoryFiles ?? [],
                memoryFilesLoading: state.learningMemoryFilesLoading ?? false,
                selectedMemoryFile: state.learningSelectedMemoryFile ?? null,
                selectedMemoryContent: state.learningSelectedMemoryContent ?? null,
                onSectionChange: (section) => {
                  state.learningActiveSection = section;
                  // Auto-load memory files when entering memory section
                  if (
                    section === "memory" &&
                    !(state.learningMemoryFiles?.length > 0) &&
                    !state.learningMemoryFilesLoading
                  ) {
                    const agentId =
                      state.agentsSelectedId ??
                      state.agentsList?.defaultId ??
                      state.agentsList?.agents?.[0]?.id;
                    if (agentId) {
                      void loadMemoryFiles(state, agentId);
                    }
                  }
                  // Auto-load knowledge sources when entering knowledge section
                  if (section === "knowledge" && !state.knowledgeSourcesLoading) {
                    void loadKnowledgeSources(state).then(() => app.requestUpdate());
                  }
                },
                onRefresh: () => {
                  const agentId =
                    state.agentsSelectedId ??
                    state.agentsList?.defaultId ??
                    state.agentsList?.agents?.[0]?.id;
                  if (agentId) {
                    void loadLearningData(state, agentId);
                  }
                },
                onInitModule: (moduleKey) => {
                  const agentId =
                    state.agentsSelectedId ??
                    state.agentsList?.defaultId ??
                    state.agentsList?.agents?.[0]?.id;
                  if (agentId) {
                    void initLearningModule(state, agentId, moduleKey);
                  }
                },
                onInitAll: () => {
                  const agentId =
                    state.agentsSelectedId ??
                    state.agentsList?.defaultId ??
                    state.agentsList?.agents?.[0]?.id;
                  if (agentId) {
                    void initAllModules(state, agentId);
                  }
                },
                onLoadMemoryFiles: () => {
                  const agentId =
                    state.agentsSelectedId ??
                    state.agentsList?.defaultId ??
                    state.agentsList?.agents?.[0]?.id;
                  if (agentId) {
                    void loadMemoryFiles(state, agentId);
                  }
                },
                onSelectMemoryFile: (filePath) => {
                  void loadMemoryFileContent(state, filePath);
                },
                // Knowledge Sources integration
                knowledgeSubTab: state.learningKnowledgeSubTab ?? "sources",
                knowledgeSourcesLoading: state.knowledgeSourcesLoading ?? false,
                knowledgeSourcesError: state.knowledgeSourcesError ?? null,
                knowledgeSources: state.knowledgeSources ?? [],
                knowledgeSourceEditing: state.knowledgeSourceEditing ?? null,
                knowledgeSourceFormOpen: state.knowledgeSourceFormOpen ?? false,
                knowledgeSourceCollecting: state.knowledgeSourceCollecting ?? null,
                onKnowledgeSubTabChange: (tab) => {
                  state.learningKnowledgeSubTab = tab;
                  if (tab === "sources" && !(state.knowledgeSources?.length > 0)) {
                    void loadKnowledgeSources(state);
                  }
                  app.requestUpdate();
                },
                onKnowledgeSourceRefresh: () => {
                  void loadKnowledgeSources(state).then(() => app.requestUpdate());
                },
                onKnowledgeSourceAdd: () => {
                  state.knowledgeSourceEditing = null;
                  state.knowledgeSourceFormOpen = true;
                  app.requestUpdate();
                },
                onKnowledgeSourceEdit: (source) => {
                  state.knowledgeSourceEditing = source;
                  state.knowledgeSourceFormOpen = true;
                  app.requestUpdate();
                },
                onKnowledgeSourceDelete: (id) => {
                  void deleteKnowledgeSource(state, id).then(() => app.requestUpdate());
                },
                onKnowledgeSourceCollect: (id) => {
                  void collectKnowledgeSource(state, id).then(() => app.requestUpdate());
                },
                onKnowledgeSourceToggle: (id, enabled) => {
                  void updateKnowledgeSource(state, id, { enabled }).then(() =>
                    app.requestUpdate(),
                  );
                },
                onKnowledgeSourceFormClose: () => {
                  state.knowledgeSourceFormOpen = false;
                  state.knowledgeSourceEditing = null;
                  app.requestUpdate();
                },
                onKnowledgeSourceFormSave: (data: Partial<KnowledgeSourceUI>) => {
                  const isEdit = !!(data as { id?: string }).id;
                  const handler = isEdit
                    ? updateKnowledgeSource(state, (data as { id: string }).id, data)
                    : createKnowledgeSource(state, data);
                  void handler.then((ok) => {
                    if (ok) {
                      state.knowledgeSourceFormOpen = false;
                      state.knowledgeSourceEditing = null;
                    }
                    app.requestUpdate();
                  });
                },
              })
            : nothing
        }

        ${
          state.tab === "mind"
            ? renderMind({
                connected: state.connected,
                loading:
                  (state as any).mindStatusLoading ||
                  (state as any).mindAnalyticsLoading ||
                  (state as any).mindHistoryLoading ||
                  false,
                error: (state as any).mindStatusError ?? null,
                status: (state as any).mindStatus ?? null,
                history: (state as any).mindHistory ?? null,
                thoughts: (state as any).mindThoughts ?? null,
                identity: (state as any).mindIdentity ?? null,
                analytics: (state as any).mindAnalytics ?? null,
                historyError: (state as any).mindHistoryError ?? null,
                thoughtsError: (state as any).mindThoughtsError ?? null,
                identityError: (state as any).mindIdentityError ?? null,
                analyticsError: (state as any).mindAnalyticsError ?? null,
                activeSection: (state as any).mindActiveSection ?? "overview",
                onSectionChange: (section) => {
                  (state as any).mindActiveSection = section;
                  void loadMindSectionData(state as any, section);
                },
                onRefresh: () => {
                  void loadMindSectionData(state as any);
                },
              })
            : nothing
        }

        ${
          state.tab === "spawn-monitor"
            ? renderSpawnMonitor({
                loading: state.subagentsStatusLoading,
                error: state.subagentsStatusError,
                status: state.subagentsStatus,
                events: state.spawnMonitorEvents,
                autoRefresh: state.spawnMonitorAutoRefresh,
                onRefresh: () => {
                  void loadSubagentsStatus(
                    state as unknown as Parameters<typeof loadSubagentsStatus>[0],
                  );
                },
                onToggleAutoRefresh: () => {
                  state.spawnMonitorAutoRefresh = !state.spawnMonitorAutoRefresh;
                  if (state.spawnMonitorAutoRefresh) {
                    if (state.spawnMonitorRefreshTimer) {
                      clearInterval(state.spawnMonitorRefreshTimer);
                    }
                    state.spawnMonitorRefreshTimer = setInterval(() => {
                      void loadSubagentsStatus(
                        state as unknown as Parameters<typeof loadSubagentsStatus>[0],
                      );
                    }, 5000);
                    void loadSubagentsStatus(
                      state as unknown as Parameters<typeof loadSubagentsStatus>[0],
                    );
                  } else {
                    if (state.spawnMonitorRefreshTimer) {
                      clearInterval(state.spawnMonitorRefreshTimer);
                      state.spawnMonitorRefreshTimer = null;
                    }
                  }
                },
                onClearEvents: () => {
                  state.spawnMonitorEvents = [];
                },
              })
            : nothing
        }

        ${
          state.tab === "nodes"
            ? renderNodes({
                loading: state.nodesLoading,
                nodes: state.nodes,
                devicesLoading: state.devicesLoading,
                devicesError: state.devicesError,
                devicesList: state.devicesList,
                configForm:
                  state.configForm ??
                  (state.configSnapshot?.config as Record<string, unknown> | null),
                configLoading: state.configLoading,
                configSaving: state.configSaving,
                configDirty: state.configFormDirty,
                configFormMode: state.configFormMode,
                execApprovalsLoading: state.execApprovalsLoading,
                execApprovalsSaving: state.execApprovalsSaving,
                execApprovalsDirty: state.execApprovalsDirty,
                execApprovalsSnapshot: state.execApprovalsSnapshot,
                execApprovalsForm: state.execApprovalsForm,
                execApprovalsSelectedAgent: state.execApprovalsSelectedAgent,
                execApprovalsTarget: state.execApprovalsTarget,
                execApprovalsTargetNodeId: state.execApprovalsTargetNodeId,
                onRefresh: () => loadNodes(state),
                onDevicesRefresh: () => loadDevices(state),
                onDeviceApprove: (requestId) => approveDevicePairing(state, requestId),
                onDeviceReject: (requestId) => rejectDevicePairing(state, requestId),
                onDeviceRotate: (deviceId, role, scopes) =>
                  rotateDeviceToken(state, { deviceId, role, scopes }),
                onDeviceRevoke: (deviceId, role) => revokeDeviceToken(state, { deviceId, role }),
                onLoadConfig: () => loadConfig(state),
                onLoadExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return loadExecApprovals(state, target);
                },
                onBindDefault: (nodeId) => {
                  if (nodeId) {
                    updateConfigFormValue(state, ["tools", "exec", "node"], nodeId);
                  } else {
                    removeConfigFormValue(state, ["tools", "exec", "node"]);
                  }
                },
                onBindAgent: (agentIndex, nodeId) => {
                  const basePath = ["agents", "list", agentIndex, "tools", "exec", "node"];
                  if (nodeId) {
                    updateConfigFormValue(state, basePath, nodeId);
                  } else {
                    removeConfigFormValue(state, basePath);
                  }
                },
                onSaveBindings: () => saveConfig(state),
                onExecApprovalsTargetChange: (kind, nodeId) => {
                  state.execApprovalsTarget = kind;
                  state.execApprovalsTargetNodeId = nodeId;
                  state.execApprovalsSnapshot = null;
                  state.execApprovalsForm = null;
                  state.execApprovalsDirty = false;
                  state.execApprovalsSelectedAgent = null;
                },
                onExecApprovalsSelectAgent: (agentId) => {
                  state.execApprovalsSelectedAgent = agentId;
                },
                onExecApprovalsPatch: (path, value) =>
                  updateExecApprovalsFormValue(state, path, value),
                onExecApprovalsRemove: (path) => removeExecApprovalsFormValue(state, path),
                onSaveExecApprovals: () => {
                  const target =
                    state.execApprovalsTarget === "node" && state.execApprovalsTargetNodeId
                      ? { kind: "node" as const, nodeId: state.execApprovalsTargetNodeId }
                      : { kind: "gateway" as const };
                  return saveExecApprovals(state, target);
                },
              })
            : nothing
        }

        ${
          state.tab === "chat"
            ? renderChat({
                sessionKey: state.sessionKey,
                onSessionKeyChange: (next) => {
                  state.sessionKey = next;
                  state.chatMessage = "";
                  state.chatAttachments = [];
                  state.chatStream = null;
                  state.chatStreamStartedAt = null;
                  state.chatRunId = null;
                  state.chatQueue = [];
                  state.resetToolStream();
                  state.resetChatScroll();
                  state.applySettings({
                    ...state.settings,
                    sessionKey: next,
                    lastActiveSessionKey: next,
                  });
                  void state.loadAssistantIdentity();
                  void loadChatHistory(state);
                  void refreshChatAvatar(state);
                },
                thinkingLevel: state.chatThinkingLevel,
                showThinking,
                loading: state.chatLoading,
                sending: state.chatSending,
                compactionStatus: state.compactionStatus,
                assistantAvatarUrl: chatAvatarUrl,
                messages: state.chatMessages,
                toolMessages: state.chatToolMessages,
                stream: state.chatStream,
                streamStartedAt: state.chatStreamStartedAt,
                draft: state.chatMessage,
                queue: state.chatQueue,
                connected: state.connected,
                canSend: state.connected,
                disabledReason: chatDisabledReason,
                error: state.lastError,
                sessions: state.sessionsResult,
                focusMode: chatFocus,
                onRefresh: () => {
                  state.resetToolStream();
                  return Promise.all([loadChatHistory(state), refreshChatAvatar(state)]);
                },
                onToggleFocusMode: () => {
                  if (state.onboarding) {
                    return;
                  }
                  state.applySettings({
                    ...state.settings,
                    chatFocusMode: !state.settings.chatFocusMode,
                  });
                },
                onChatScroll: (event) => state.handleChatScroll(event),
                onDraftChange: (next) => (state.chatMessage = next),
                attachments: state.chatAttachments,
                onAttachmentsChange: (next) => (state.chatAttachments = next),
                onSend: () => state.handleSendChat(),
                canAbort: Boolean(state.chatRunId),
                onAbort: () => void state.handleAbortChat(),
                onQueueRemove: (id) => state.removeQueuedMessage(id),
                onNewSession: () => state.handleSendChat("/new", { restoreDraft: true }),
                showNewMessages: state.chatNewMessagesBelow && !state.chatManualRefreshInFlight,
                onScrollToBottom: () => state.scrollToBottom(),
                // Sidebar props for tool output viewing
                sidebarOpen: state.sidebarOpen,
                sidebarContent: state.sidebarContent,
                sidebarError: state.sidebarError,
                splitRatio: state.splitRatio,
                onOpenSidebar: (content: string) => state.handleOpenSidebar(content),
                onCloseSidebar: () => state.handleCloseSidebar(),
                onSplitRatioChange: (ratio: number) => state.handleSplitRatioChange(ratio),
                assistantName: state.assistantName,
                assistantAvatar: state.assistantAvatar,
              })
            : nothing
        }

        ${
          state.tab === "config"
            ? renderConfig({
                raw: state.configRaw,
                originalRaw: state.configRawOriginal,
                valid: state.configValid,
                issues: state.configIssues,
                loading: state.configLoading,
                saving: state.configSaving,
                applying: state.configApplying,
                updating: state.updateRunning,
                connected: state.connected,
                schema: state.configSchema,
                schemaLoading: state.configSchemaLoading,
                uiHints: state.configUiHints,
                formMode: state.configFormMode,
                formValue: state.configForm,
                originalValue: state.configFormOriginal,
                searchQuery: state.configSearchQuery,
                activeSection: state.configActiveSection,
                activeSubsection: state.configActiveSubsection,
                onRawChange: (next) => {
                  state.configRaw = next;
                },
                onFormModeChange: (mode) => (state.configFormMode = mode),
                onFormPatch: (path, value) => updateConfigFormValue(state, path, value),
                onSearchChange: (query) => (state.configSearchQuery = query),
                onSectionChange: (section) => {
                  state.configActiveSection = section;
                  state.configActiveSubsection = null;
                },
                onSubsectionChange: (section) => (state.configActiveSubsection = section),
                onReload: () => loadConfig(state),
                onSave: () => saveConfig(state),
                onApply: () => applyConfig(state),
                onUpdate: () => runUpdate(state),
              })
            : nothing
        }

        ${
          state.tab === "terminal"
            ? renderTerminal({
                connected: state.connected,
                input: state.terminalInput,
                history: state.terminalHistory,
                running: state.terminalRunning,
                state,
                onInputChange: (value) => (state.terminalInput = value),
                onExec: () => execTerminalCommand(state),
                onClear: () => clearTerminalHistory(state),
              })
            : nothing
        }

        ${
          state.tab === "files"
            ? renderFiles({
                connected: state.connected,
                currentPath: state.filesCurrentPath,
                loading: state.filesLoading,
                error: state.filesError,
                list: state.filesList,
                showHidden: state.filesShowHidden,
                openFile: state.filesOpenFile,
                openFileLoading: state.filesOpenFileLoading,
                openFileError: state.filesOpenFileError,
                editDraft: state.filesEditDraft,
                editSaving: state.filesEditSaving,
                onNavigate: (path) => loadDirectory(state, path),
                onOpenFile: (path) => openFile(state, path),
                onCloseFile: () => closeFile(state),
                onEditDraftChange: (content) => (state.filesEditDraft = content),
                onSave: () => saveFile(state),
                onDelete: (path) => deleteItem(state, path),
                onToggleHidden: () => {
                  state.filesShowHidden = !state.filesShowHidden;
                  loadDirectory(state);
                },
                onRefresh: () => loadDirectory(state),
              })
            : nothing
        }

        ${
          state.tab === "heartbeat"
            ? renderHeartbeat({
                loading: state.hbLoading,
                error: state.hbError,
                history: state.hbHistory as any,
                liveEvents: state.hbLiveEvents as any,
                stats: state.hbStats as any,
                paused: state.hbPaused,
                timeRange: state.hbTimeRange,
                onRefresh: () => {
                  loadHeartbeatAnalytics(state as any);
                },
                onTogglePause: () => {
                  state.hbPaused = !state.hbPaused;
                },
                onTimeRangeChange: (range) => {
                  state.hbTimeRange = range;
                  loadHeartbeatAnalytics(state as any);
                },
              })
            : nothing
        }

        ${
          state.tab === "debug"
            ? renderDebug({
                loading: state.debugLoading,
                status: state.debugStatus,
                health: state.debugHealth,
                models: state.debugModels,
                heartbeat: state.debugHeartbeat,
                eventLog: state.eventLog,
                callMethod: state.debugCallMethod,
                callParams: state.debugCallParams,
                callResult: state.debugCallResult,
                callError: state.debugCallError,
                onCallMethodChange: (next) => (state.debugCallMethod = next),
                onCallParamsChange: (next) => (state.debugCallParams = next),
                onRefresh: () => loadDebug(state),
                onCall: () => callDebugMethod(state),
              })
            : nothing
        }

        ${
          state.tab === "logs"
            ? renderLogs({
                loading: state.logsLoading,
                error: state.logsError,
                file: state.logsFile,
                entries: state.logsEntries,
                filterText: state.logsFilterText,
                levelFilters: state.logsLevelFilters,
                autoFollow: state.logsAutoFollow,
                truncated: state.logsTruncated,
                onFilterTextChange: (next) => (state.logsFilterText = next),
                onLevelToggle: (level, enabled) => {
                  state.logsLevelFilters = { ...state.logsLevelFilters, [level]: enabled };
                },
                onToggleAutoFollow: (next) => (state.logsAutoFollow = next),
                onRefresh: () => loadLogs(state, { reset: true }),
                onExport: (lines, label) => state.exportLogs(lines, label),
                onScroll: (event) => state.handleLogsScroll(event),
              })
            : nothing
        }

        ${
          state.tab === "activity-log"
            ? renderActivityLog({
                loading: state.actLogLoading,
                error: state.actLogError,
                entries: state.actLogEntries,
                stats: state.actLogStats,
                filterText: state.actLogFilterText,
                category: state.actLogCategory,
                level: state.actLogLevel,
                channel: state.actLogChannel,
                autoRefresh: state.actLogAutoRefresh,
                selectedEntry: state.actLogSelectedEntry,
                statsLoading: state.actLogStatsLoading,
                onFilterTextChange: (next) => (state.actLogFilterText = next),
                onCategoryChange: (next) => {
                  state.actLogCategory = next;
                  loadActivityLog(state);
                },
                onLevelChange: (next) => {
                  state.actLogLevel = next;
                  loadActivityLog(state);
                },
                onChannelChange: (next) => (state.actLogChannel = next),
                onToggleAutoRefresh: (next) => {
                  state.actLogAutoRefresh = next;
                  if (next && !state.actLogRefreshTimer) {
                    state.actLogRefreshTimer = setInterval(() => loadActivityLog(state), 5000);
                  } else if (!next && state.actLogRefreshTimer) {
                    clearInterval(state.actLogRefreshTimer);
                    state.actLogRefreshTimer = null;
                  }
                },
                onRefresh: () => loadActivityLog(state),
                onLoadStats: () => loadActivityLogStats(state),
                onSelectEntry: (entry) => (state.actLogSelectedEntry = entry),
                onExport: () => {
                  const json = JSON.stringify(state.actLogEntries, null, 2);
                  const blob = new Blob([json], { type: "application/json" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                },
              })
            : nothing
        }
      </main>
      ${renderExecApprovalPrompt(state)}
      ${renderGatewayUrlConfirmation(state)}
      ${renderToasts()}
      ${renderOnboarding(state.settings, (state as any)._onboardingStep ?? "welcome", {
        onLocaleChange: (locale) => {
          state.applySettings({ ...state.settings, locale });
        },
        onThemeChange: (theme) => {
          state.applySettings({ ...state.settings, theme });
        },
        onNavModeChange: (mode) => {
          state.applySettings({ ...state.settings, navMode: mode });
        },
        onComplete: () => {
          state.applySettings({ ...state.settings, onboardingDone: true });
        },
        onSkip: () => {
          state.applySettings({ ...state.settings, onboardingDone: true });
        },
      })}
    </div>
  `;
}
